import type { ArgumentsSchema } from '@lighthouse/core'
import {
    type FieldType,
    FlowSelectSourceType,
    VariableType,
    WechatProfitSharingPlatformEnum,
    WechatProfitSharingRelationTypeEnum,
    WechatProfitSharingTypeEnum
} from '@lighthouse/core'
import { nanoid } from '@lighthouse/tools'

import { DEFAULT_VALUE_VARIABLE } from '../components/Variable/constant'
import type { FlowNode, NodeOption, NodeTypes, SenderAccountType } from '../types'
import {
    ActionDataNodeType,
    AINodeType,
    ClassNode,
    ConditionRunType,
    EMAIL_EDIT_TYPE,
    EndNodeType,
    FlowControlNodeType,
    LoopErrorHandlerEnum,
    LoopTypeEnum,
    NoticeNodeType,
    OperatorNodeType,
    OtherPlatformNodeType,
    PARAGRAPH_WIDTH_TYPE,
    SearchApiResultType,
    TriggerNodeType,
    WorkflowType
} from '../types'
import { getElseHandleId } from '../utils'

export const ANONYMOUS_PARAM_NAME = '未命名参数'

export const updateWorkflowErrorMessage = '无法更新工作流，请检查配置后重试'

export const enableWorkflowErrorMessage = '无法启用工作流，请检查配置后重试'

export const disableWorkflowErrorMessage = '禁用失败，请稍后重试'

export const LLMArgId = 'text'
export const SearchArgTitleId = 'label'
export const SearchArgContentId = 'value'

export const OutTradeNo = 'outTradeNo'
// WeChat Refund IDs
export const RefundIdId = 'refundId'
export const OutRefundNoId = 'outRefundNo'
// export const TransactionIdId = 'transactionId'
export const ChannelId = 'channel'
export const UserReceivedAccountId = 'userReceivedAccount'
export const SuccessTimeId = 'successTime'
export const CreateTimeId = 'createTime'
export const StatusId = 'status'
export const FundsAccountId = 'fundsAccount'

// WeChat Profit Sharing IDs
export const TypeId = 'type'
export const AccountId = 'account'
export const NameId = 'name'
export const RelationTypeId = 'relationType'
export const CustomRelationId = 'customRelation'
export const OrderIdId = 'orderId'
export const OutOrderNoId = 'outOrderNo'
export const StateId = 'state'
export const UnSplitAmountId = 'unsplitAmount'

// Import Excel IDs
export const ImportExcelTotalCountId = 'total_count'
export const ImportExcelImportedRecordsId = 'imported_record_ids'
export const ImportExcelIsImportAllId = 'is_import_all'
export const ImportExcelReasonsForFailedDataId = 'reasons_for_failed_data'

// Webhook Trigger IDs
export const WebhookBody = 'webhookBody'

export const MiniProgramCode = 'image_url'

export const NODE_CARD_WIDTH = 238

export const NODE_CARD_DEFAULT_HEIGHT = 52

export const X_OFFSET = 60
export const Y_OFFSET = 39

export const IGNORE_WATCH_NODE_FIELD_TYPES = new Set<FieldType>(['notes', 'formula', 'aggregation'])

export const SEND_EMAIL_WIDTH: Record<PARAGRAPH_WIDTH_TYPE, number | string> = {
    NORMAL: 480,
    WIDER: 560, // 较宽
    FULL: '100%' // 全宽
}

export const WorkflowTextMap: Record<string, string> = {
    automation: '自动化',
    action: '动作流',
    subProcess: '子流程',
    all: '全部流程'
}

export const triggerNodeTypeOptions: NodeOption[] = [
    {
        label: '当记录被创建时',
        value: TriggerNodeType.CREATE_RECORD_TRIGGER,
        // icon: 'PencilSimple',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '当以上指定的其中一个字段更新时将触发流程，如未指定则表示任何字段更新时都会触发',
        auth: [WorkflowType.AUTOMATION]
    },
    {
        label: '当记录满足指定条件时',
        value: TriggerNodeType.MATCH_CONDITION_TRIGGER,
        // icon: 'Workflow',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '当以上指定的其中一个字段更新时将触发流程，如未指定则表示任何字段更新时都会触发',
        auth: [WorkflowType.AUTOMATION]
    },
    {
        label: '当记录被更新时',
        value: TriggerNodeType.UPDATE_RECORD_TRIGGER,
        // icon: 'FlowArrow',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '当以上指定的其中一个字段更新时将触发流程，如未指定则表示任何字段更新时都会触发',
        auth: [WorkflowType.AUTOMATION]
    },
    {
        label: '定时触发',
        value: TriggerNodeType.SCHEDULED_TRIGGER,
        // icon: 'FlowArrow',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '当以上指定的其中一个字段更新时将触发流程，如未指定则表示任何字段更新时都会触发',
        auth: [WorkflowType.AUTOMATION]
    },
    {
        label: 'Webhook触发',
        value: TriggerNodeType.WEBHOOK_TRIGGER,
        // icon: 'FlowArrow',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '通过Webhook触发流程',
        auth: [WorkflowType.AUTOMATION]
    },
    // 子流程出发节点
    {
        label: '开始',
        value: TriggerNodeType.ARG_TRIGGER,
        // icon: 'Reorder',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '循环节点使用时，会触发子流程',
        auth: [WorkflowType.SUB_PROCESS]
    },
    // 动作流触发节点
    {
        label: '动作触发',
        value: TriggerNodeType.CLICK_TRIGGER,
        // icon: 'MouseClick',
        color: 'var(--color-white)',
        background: 'var(--color-main)',
        desc: '通过动作触发流程',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION]
    },
    {
        label: '开始',
        value: TriggerNodeType.EMPTY_TRIGGER,
        background: 'var(--color-main)',
        // icon: 'FlowArrow',
        color: 'var(--color-white)',
        desc: '',
        auth: [WorkflowType.AUTOMATION]
    }
]

/**
 * NodeCard start
 */

// AI
export const aiNodeTypeOptions: NodeOption[] = [
    {
        label: '大语言模型(LLM)',
        value: AINodeType.LLM_ACTION,
        desc: '调用大语言模型回答问题或者对自然语言进行处理。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'LLM',
        background: '#5264F0'
    },
    {
        label: '参数提取',
        value: AINodeType.PARAM_EXTRACT_ACTION,
        desc: '利用 LLM从自然语言内推理提取出结构化参数，用于后置的节点调用。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Blur',
        background: '#0D84F9'
    },
    {
        label: '代码执行',
        value: AINodeType.CODE_ACTION,
        desc: '执行一段 Python代码实现自定义逻辑',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Code',
        background: '#0D84F9'
    }
]

// 数据处理

export const dataNodeTypeOptions: NodeOption[] = [
    {
        label: '创建单条记录',
        value: ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION,
        desc: '向数据表创建一条记录，同时可设置记录的值',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Add',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    },
    {
        label: '创建多条记录',
        value: ActionDataNodeType.CREATE_RECORD_ACTION,
        desc: '向数据表创建多条记录，同时可设置记录的值',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'AddList',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    },
    {
        label: '更新记录',
        value: ActionDataNodeType.UPDATE_RECORD_ACTION,
        desc: '更新单条或多条记录',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Angle',
        color: 'var(--color-white)',
        background: '#2E90FA'
    },
    {
        label: '删除单条记录',
        value: ActionDataNodeType.DELETE_RECORD,
        desc: '删除一条记录',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Trash',
        color: 'var(--color-white)',
        background: 'var(--color-red-500)'
    },
    {
        label: '查询单条记录',
        value: ActionDataNodeType.FIND_SINGLE_RECORD_ACTION,
        desc: '通过条件向数据表查询一条记录',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'Search',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    },
    {
        label: '查询多条记录',
        value: ActionDataNodeType.FIND_RECORD_ACTION,
        desc: '通过条件向数据表查询多条记录',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'QueryData',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    },
    {
        label: '导入Excel到数据表',
        value: ActionDataNodeType.IMPORT_EXCEL,
        desc: '将 Excel 数据导入到指定数据表',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'FileXsl',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    },
    {
        label: '批量导入文件',
        value: ActionDataNodeType.BATCH_IMPORT_FILE,
        desc: '将文件批量导入到数据表的附件字段',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'UploadSimple',
        color: 'var(--color-white)',
        background: 'var(--color-green-500)'
    }
]

// 通知
export const noticeNodeTypeOptions: NodeOption[] = [
    {
        label: '发送站内信',
        value: NoticeNodeType.STATION_MESSAGE_ACTION,
        desc: '给人员发送站内信',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'StationMessage',
        background: 'var(--color-purple-500)'
    }
]

// 流程控制
export const flowControlNodeTypeOptions: NodeOption[] = [
    {
        label: '条件分支',
        value: FlowControlNodeType.CONDITION,
        desc: '允许你将流程拆分成多个分支',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'ConditionalBranch',
        background: '#2E90FA'
    },
    {
        label: '循环',
        value: FlowControlNodeType.LOOP_ACTION,
        desc: '根据循环规则来循环运行某条子流程',
        auth: [WorkflowType.AUTOMATION],
        // icon: 'Repeat',
        background: '#2E90FA'
    },
    {
        label: '执行子流程',
        value: FlowControlNodeType.HANDLE_SUB_PROCESS,
        desc: '执行一条子流程',
        auth: [WorkflowType.ACTION],
        // icon: 'Reorder',
        background: '#2E90FA'
    },
    {
        label: '发送websocket',
        value: FlowControlNodeType.SEND_WEBSOCKET,
        desc: '发送 Websocket 事件',
        auth: [WorkflowType.AUTOMATION],
        background: '#2E90FA'
    },
    {
        label: '结束',
        value: EndNodeType.END,
        desc: '定义一个流程的结束',
        auth: [],
        // icon: 'Golf',
        // icon: nodeTypeIconMap[EndNodeType.END],
        background: '#FC8212'
    }
]

// 操作
export const operatorNodeTypeOptions: NodeOption[] = [
    {
        label: '打开页面',
        value: OperatorNodeType.OPEN_PAGE,
        desc: '允许你打开页面',
        auth: [WorkflowType.ACTION],
        // icon: 'OpenPage',
        background: '#4E5BA6'
    },
    {
        label: '打开链接',
        desc: '打开一个链接',
        value: OperatorNodeType.OPEN_LINK,
        auth: [WorkflowType.ACTION],
        // icon: 'PropertyLink',
        background: '#4E5BA6'
    },
    {
        label: '显示提示',
        value: OperatorNodeType.NOTIFICATION,
        desc: '显示一个提示',
        auth: [WorkflowType.ACTION],
        // icon: 'Bell',
        background: '#4E5BA6'
    },
    {
        label: '拨打电话',
        value: OperatorNodeType.CALL,
        desc: '拨打电话',
        auth: [WorkflowType.ACTION],
        // icon: 'PropertyPhone',
        background: '#4E5BA6'
    },
    {
        label: '返回上一步',
        value: OperatorNodeType.GO_BACK,
        desc: '返回上一步',
        auth: [WorkflowType.ACTION],
        // icon: 'Undo',
        background: '#4E5BA6'
    },
    {
        label: '复制到剪切板',
        value: OperatorNodeType.COPY,
        desc: '拷贝数据到剪切板',
        auth: [WorkflowType.ACTION],
        // icon: 'Copy',
        background: '#4E5BA6'
    },
    {
        label: '关闭弹窗',
        value: OperatorNodeType.CLOSE_FLOAT,
        desc: '关闭最近打开的一个弹窗',
        auth: [WorkflowType.ACTION],
        // icon: 'CloseOverlay',
        background: '#4E5BA6'
    },
    {
        label: '下载',
        value: OperatorNodeType.DOWNLOAD_FILE,
        desc: '下载文件',
        auth: [WorkflowType.ACTION],
        // icon: 'Download',
        background: '#4E5BA6'
    },
    {
        label: '刷新页面数据',
        value: OperatorNodeType.REFRESH_PAGE,
        desc: '刷新整个页面的数据',
        auth: [WorkflowType.ACTION],
        // icon: 'UpdatePage',
        background: '#4E5BA6'
    },
    {
        label: '刷新用户权限',
        value: OperatorNodeType.REFRESH_USER_PERMISSIONS,
        desc: '刷新当前用户的权限数据',
        auth: [WorkflowType.ACTION],
        // icon: 'PermissionSetting',
        background: '#4E5BA6'
    }
]

// 微信公众号
export const wxAccountNodeTypeOptions: NodeOption[] = [
    {
        label: '发送模板消息',
        value: OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION,
        desc: '发送微信模版消息',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    }
]

// 微信商户号
export const wxMerchantNodeTypeOptions: NodeOption[] = [
    {
        label: '微信支付',
        value: OtherPlatformNodeType.WECHAT_PAY,
        desc: '通过调用微信支付提供的接口，在支付场景中调起微信支付模块完成收款。',
        auth: [WorkflowType.ACTION],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '申请退款',
        value: OtherPlatformNodeType.WECHAT_REFUND,
        desc: '可通过此工具将订单的支付金额的全部或部分原路退还至用户。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '查询单笔退款',
        value: OtherPlatformNodeType.WECHAT_REFUND_QUERY,
        desc: '查询单笔退款订单，提交退款申请后，推荐每间隔1分钟调用该接口查询一次退款状态，若超过5分钟仍是退款处理中状态，建议开始逐步衰减查询频率(比如之后间隔5分钟、10分钟、20分钟、30分钟……查询一次)。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '添加分账接收方',
        value: OtherPlatformNodeType.WECHAT_ADD_RECEIVER,
        desc: '商户发起添加分账接收方请求，建立分账接收方列表。后续可通过发起分账请求，将分账方商户结算后的资金，分到该分账接收方',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '删除分账接收方',
        value: OtherPlatformNodeType.WECHAT_DELETE_RECEIVER,
        desc: '商户发起删除分账接收方请求。删除后，不支持将分账方商户结算后的资金，分到该分账接收方',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '请求分账',
        value: OtherPlatformNodeType.WECHAT_PROFIT_SHARING,
        desc: '微信订单支付成功后，商户发起分账请求，将结算后的资金分到分账接收方。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '查询分账结果',
        value: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY,
        desc: '发起分账请求后，可调用此工具查询分账结果。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '查询待剩余待分金额',
        value: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT,
        desc: '可调用此工具查询订单剩余待分金额。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WechatPay',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    }
]

// 微信小程序
export const wxMiniProgramNodeTypeOptions: NodeOption[] = [
    {
        label: '小程序授权订阅消息',
        value: OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_ACTION,
        desc: '小程序授权订阅消息',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '发送小程序订阅消息',
        value: OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION,
        desc: '发送小程序订阅消息',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '打开小程序内置地图',
        value: OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION,
        desc: '使用微信内置地图查看位置。',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '跳转到微信小程序',
        value: OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM,
        desc: '跳转到指定的小程序',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '打开微信视频号视频',
        value: OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY,
        desc: '跳转打开视频号视频',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '打开微信客服',
        value: OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE,
        desc: '打开微信客服消息聊天界面。',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '打开微信音频播放器',
        value: OtherPlatformNodeType.WECHAT_OPEN_AUDIO_PLAYER,
        desc: '打开微信音频播放器',
        auth: [WorkflowType.ACTION],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '生成小程序码',
        value: OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE,
        desc: '生成小程序码',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'WeChatLogo',
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '发货信息录入',
        value: OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO,
        desc: '运营发货后写入微信，无需登录小程序后台。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '查询订单发货状态',
        value: OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING,
        desc: '按支付单查询微信发货状态，用于订单详情同步。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '查询订单列表',
        value: OtherPlatformNodeType.WECHAT_GET_ORDER_LIST,
        desc: '按支付者或状态拉取支付单列表，用于列表页批量同步。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    },
    {
        label: '传运单',
        value: OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL,
        desc: '把运单交给微信跟踪，在揽件/派件/签收给用户推物流消息。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        background: '#F1FFF9',
        color: 'var(--color-green-500)'
    }
]

// search api
export const searchApiNodeTypeOptions: NodeOption[] = [
    {
        label: 'Google Search API',
        value: OtherPlatformNodeType.GOOGLE_SEARCH_ACTION,
        desc: '一种从 Google 搜索引擎检索答案框、知识图、片段和网页的工具。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'SearchAPI',
        background: '#6986EB',
        color: '#00001B'
    },
    {
        label: 'Baidu Search API',
        value: OtherPlatformNodeType.BAIDU_SEARCH_ACTION,
        desc: '一种从百度搜索引擎检索答案框、知识图、片段和网页的工具。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'SearchAPI',
        background: '#6986EB',
        color: '#00001B'
    },
    {
        label: 'Bing Search API',
        value: OtherPlatformNodeType.BING_SEARCH_ACTION,
        desc: '一种从必应搜索引擎检索答案框、知识图、片段和网页的工具。',
        auth: [WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'SearchAPI',
        background: '#6986EB',
        color: '#00001B'
    }
]

// 钉钉
export const dingNodeTypeOptions: NodeOption[] = [
    {
        label: '机器人发送群消息',
        value: OtherPlatformNodeType.DINGTALK_ROBOT_ACTION,
        desc: '',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'BrandLogoDingding',
        background: '#E9F2FB',
        color: '#2E90FA'
    }
]

// 企业微信
export const enterpriseWeChatNodeTypeOptions: NodeOption[] = [
    {
        label: '机器人发送群消息',
        value: OtherPlatformNodeType.WX_ROBOT_ACTION,
        desc: '通过机器人给钉钉群发送群消息',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'BrandLogoqiyeweixin',
        background: '#ECEFF4',
        color: '#0082EF'
    }
]

// 阿里云
export const aLiYunNodeTypeOptions: NodeOption[] = [
    {
        label: '发送短信',
        value: OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION,
        desc: '通过阿里云短信服务发送短信',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        // icon: 'aliyun',
        background: '#FFF5EF',
        color: '#ED732E'
    }
]

// 腾讯企业邮箱
export const tencentEmailNodeTypeOptions: NodeOption[] = [
    {
        label: '发送邮件',
        value: OtherPlatformNodeType.SEND_EMAIL_ACTION,
        desc: '通过腾讯企业邮箱发送邮件',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        subType: 'WE_COM_MAIL',
        // icon: 'BrandLogoWeCom',
        background: '#F0F6FD',
        color: '#2883EC'
    }
]

// 126网易免费邮
export const wangYiEmailNodeTypeOptions: NodeOption[] = [
    {
        label: '发送邮件',
        value: OtherPlatformNodeType.SEND_EMAIL_ACTION,
        desc: '通过126网页免费邮箱发送邮件',
        auth: [WorkflowType.ACTION, WorkflowType.AUTOMATION, WorkflowType.SUB_PROCESS],
        subType: 'NET_EASE_126_MAIL',
        // icon: 'NetEasyFree',
        background: '#EEFFF4',
        color: '#1E7E3E'
    }
]

// 发起支付
export const stripeNodeTypeOptions: NodeOption[] = [
    {
        label: 'Stripe支付',
        value: OtherPlatformNodeType.STRIPE,
        desc: '发起Stripe支付',
        auth: [WorkflowType.ACTION],
        // icon: 'Stripe',
        background: '#F1F5FA',
        color: '#0D2138'
    }
]

// 自动化触发节点
export const triggerNodeTypeCustomOptions: NodeOption[] = triggerNodeTypeOptions.filter(
    item => item.value !== TriggerNodeType.ARG_TRIGGER && item.value !== TriggerNodeType.EMPTY_TRIGGER
)

/**
 * NodeCard end
 */

// 合并 options
export const nodeTypeOptions = [
    ...triggerNodeTypeOptions,
    ...aiNodeTypeOptions,
    ...dataNodeTypeOptions,
    ...noticeNodeTypeOptions,
    ...flowControlNodeTypeOptions,
    ...operatorNodeTypeOptions,
    ...wxAccountNodeTypeOptions,
    ...wxMerchantNodeTypeOptions,
    ...searchApiNodeTypeOptions,
    ...dingNodeTypeOptions,
    ...enterpriseWeChatNodeTypeOptions,
    ...aLiYunNodeTypeOptions,
    ...tencentEmailNodeTypeOptions,
    ...wangYiEmailNodeTypeOptions,
    ...stripeNodeTypeOptions,
    ...wxMiniProgramNodeTypeOptions
]

export const fromUpstreamOptions = [
    {
        label: '数据表',
        value: FlowSelectSourceType.DATASOURCE
    },
    {
        label: '上游节点',
        value: FlowSelectSourceType.UPSTREAM
    }
]

export const nodeTypeMap = Object.fromEntries(nodeTypeOptions.map(item => [item.value, item]))

export const getNodeInfo = (nodeType: NodeTypes) => {
    return nodeTypeMap[nodeType]
}

export const actionDefaultNodeMap: Record<NodeTypes, () => FlowNode> = {
    EMPTY_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.EMPTY_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.EMPTY_TRIGGER].label,
            config: {}
        },
        position: {
            x: 0,
            y: 0
        }
    }),
    WEBHOOK_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.WEBHOOK_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.WEBHOOK_TRIGGER].label,
            config: {
                url: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    END: () => ({
        id: '',
        type: ClassNode.END,
        data: {
            nodeType: EndNodeType.END,
            name: nodeTypeMap[EndNodeType.END].label
        },
        position: { x: 0, y: 0 }
    }),
    CREATE_RECORD_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.CREATE_RECORD_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.CREATE_RECORD_TRIGGER].label,
            config: {
                dataSourceId: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    UPDATE_RECORD_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.UPDATE_RECORD_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.UPDATE_RECORD_TRIGGER].label,
            config: {
                dataSourceId: '',
                watchedFields: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    MATCH_CONDITION_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.MATCH_CONDITION_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.MATCH_CONDITION_TRIGGER].label,
            config: {
                dataSourceId: '',
                filter: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    SCHEDULED_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.SCHEDULED_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.SCHEDULED_TRIGGER].label,
            config: {
                scheduleStartTime: new Date(),
                scheduleEndTime: new Date(),
                scheduleRule: 'ONCE'
            }
        },
        position: { x: 0, y: 0 }
    }),
    CLICK_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.CLICK_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.CLICK_TRIGGER].label,
            config: {
                args: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    WEBSOCKET_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.WEBSOCKET_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.WEBSOCKET_TRIGGER].label,
            config: {
                args: [],
                url: '',
                eventName: '',
                eventValue: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    ARG_TRIGGER: () => ({
        id: '',
        type: ClassNode.TRIGGER,
        data: {
            nodeType: TriggerNodeType.ARG_TRIGGER,
            name: nodeTypeMap[TriggerNodeType.ARG_TRIGGER].label,
            config: { args: [] }
        },
        position: { x: 0, y: 0 }
    }),
    CONDITION: () => ({
        id: '',
        type: ClassNode.CONDITION,
        data: {
            nodeType: FlowControlNodeType.CONDITION,
            name: nodeTypeMap[FlowControlNodeType.CONDITION].label,
            config: {
                filterGroup: [
                    {
                        id: nanoid(),
                        filter: {}
                    }
                ],
                elseHandleId: getElseHandleId(),
                runType: ConditionRunType.UNIQUE
            }
        },
        connectedSourceHandleIds: [],
        position: { x: 0, y: 0 }
    }),

    CREATE_SINGLE_RECORD_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION,
            name: nodeTypeMap['CREATE_SINGLE_RECORD_ACTION'].label,
            config: {
                dataSourceId: '',
                fields: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    CREATE_RECORD_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.CREATE_RECORD_ACTION,
            name: nodeTypeMap['CREATE_RECORD_ACTION'].label,
            config: {
                dataSourceId: '',
                nodeId: '',
                fields: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    UPDATE_RECORD_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.UPDATE_RECORD_ACTION,
            name: nodeTypeMap[ActionDataNodeType.UPDATE_RECORD_ACTION].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: [],
                fields: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    FIND_SINGLE_RECORD_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.FIND_SINGLE_RECORD_ACTION,
            name: nodeTypeMap[ActionDataNodeType.FIND_SINGLE_RECORD_ACTION].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    FIND_RECORD_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.FIND_RECORD_ACTION,
            name: nodeTypeMap[ActionDataNodeType.FIND_RECORD_ACTION].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    IMPORT_EXCEL: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.IMPORT_EXCEL,
            name: nodeTypeMap[ActionDataNodeType.IMPORT_EXCEL].label,
            config: {
                dataSourceId: '',
                excelSource: {},
                repeat: false,
                compareFields: [],
                requiredFields: [],
                importMode: 'skip'
            }
        },
        position: { x: 0, y: 0 }
    }),
    BATCH_IMPORT_FILE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.BATCH_IMPORT_FILE,
            name: nodeTypeMap[ActionDataNodeType.BATCH_IMPORT_FILE].label,
            config: {
                dataSourceId: '',
                attachmentFieldId: '',
                importMode: 'IMAGES',
                mappingFieldId: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    WX_ROBOT_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WX_ROBOT_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.WX_ROBOT_ACTION].label,
            config: {
                id: '',
                msgtype: 'text',
                webhook: '',
                text: {
                    editorContent: {},
                    mentionedContent: {}
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    DINGTALK_ROBOT_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.DINGTALK_ROBOT_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.DINGTALK_ROBOT_ACTION].label,
            config: {
                id: '',
                msgtype: 'text',
                webhook: '',
                secret: '',
                at: {
                    atContent: {}
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    SEND_EMAIL_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.SEND_EMAIL_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.SEND_EMAIL_ACTION].label,
            config: {
                senderAccountType: 'WE_COM_MAIL',
                title: {},
                background: {
                    type: 'color',
                    color: '#ECEFF4'
                },
                enableCc: true,
                padding: { size: [32, 32, 32, 32], unit: 'px' },
                cc: [],
                recipients: [],
                body: {
                    editType: EMAIL_EDIT_TYPE.RICHTEXT,
                    paragraph: [],
                    width: PARAGRAPH_WIDTH_TYPE.NORMAL,
                    background: {
                        type: 'color',
                        color: '#ECEFF4'
                    },

                    border: {
                        type: 'color',
                        color: '#E4E7EC',
                        mode: 'each',
                        each: [1, 1, 1, 1]
                    },
                    gap: { size: 8, unit: 'px' },
                    padding: { size: [32, 32, 32, 32], unit: 'px' }
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    STATION_MESSAGE_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: NoticeNodeType.STATION_MESSAGE_ACTION,
            name: nodeTypeMap[NoticeNodeType.STATION_MESSAGE_ACTION].label,
            config: {
                title: {},
                addresseeIds: [],
                content: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    // 动作流相关
    OPEN_PAGE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.OPEN_PAGE,
            name: nodeTypeMap[OperatorNodeType.OPEN_PAGE].label,
            config: {
                openPageId: '',
                pageOpenType: 'page'
            }
        },
        position: { x: 0, y: 0 }
    }),
    OPEN_LINK: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.OPEN_LINK,
            name: nodeTypeMap[OperatorNodeType.OPEN_LINK].label,
            config: {}
        },
        position: { x: 0, y: 0 }
    }),
    GO_BACK: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            config: {},
            nodeType: OperatorNodeType.GO_BACK,
            name: nodeTypeMap[OperatorNodeType.GO_BACK].label
        },
        position: { x: 0, y: 0 }
    }),
    NOTIFICATION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.NOTIFICATION,
            name: nodeTypeMap[OperatorNodeType.NOTIFICATION].label,
            config: {
                notificationStatus: 'INFO',
                notificationContent: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    COPY: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.COPY,
            name: nodeTypeMap[OperatorNodeType.COPY].label,
            config: {
                copyText: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    DOWNLOAD_FILE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.DOWNLOAD_FILE,
            name: nodeTypeMap[OperatorNodeType.DOWNLOAD_FILE].label,
            config: {
                fileUrl: DEFAULT_VALUE_VARIABLE
            }
        },
        position: { x: 0, y: 0 }
    }),
    ALIYUN_MESSAGE_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.ALIYUN_MESSAGE_ACTION].label,
            config: {
                id: '',
                mobileList: DEFAULT_VALUE_VARIABLE,
                signatureId: '',
                templateId: '',
                values: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_PAY: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_PAY,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_PAY].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: [],

                descFieldId: '',
                autoRedirectAfterPayment: true,
                /** 订单金额 */
                amountFieldId: '',
                succeedFieldSettings: [],
                // failedFieldSettings: []
                callbackWorkflow: {
                    workflowId: '',
                    workflowArgs: []
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_REFUND: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_REFUND,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_REFUND].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: [],
                payOrderFieldId: '',
                payAmountFieldId: '',
                refundAmountFieldId: '',
                reasonFieldId: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_REFUND_QUERY: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_REFUND_QUERY,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_REFUND_QUERY].label,
            config: {
                refundOrderId: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_ADD_RECEIVER: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_ADD_RECEIVER,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_ADD_RECEIVER].label,
            config: {
                type: WechatProfitSharingTypeEnum.MERCHANT_ID,
                platform: WechatProfitSharingPlatformEnum.WECHAT,
                account: {},
                relationType: WechatProfitSharingRelationTypeEnum.STORE
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_DELETE_RECEIVER: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_DELETE_RECEIVER,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_DELETE_RECEIVER].label,
            config: {
                type: WechatProfitSharingTypeEnum.MERCHANT_ID,
                platform: WechatProfitSharingPlatformEnum.WECHAT,
                account: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_PROFIT_SHARING: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_PROFIT_SHARING].label,
            config: {
                outTradeNo: {},
                profitSharingOrderId: {},
                isUnfreezeRemainingFund: {},
                profitSharingReceiver: {
                    nodeId: '',
                    profitSharingAccount: {},
                    profitSharingPlatform: WechatProfitSharingPlatformEnum.WECHAT,
                    profitSharingType: WechatProfitSharingTypeEnum.MERCHANT_ID,
                    profitSharingAmount: {},
                    profitSharingDescription: {}
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_PROFIT_SHARING_QUERY: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY].label,
            config: {
                profitSharingOrderId: {},
                outTradeNo: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_PROFIT_SHARING_QUERY_AMOUNT: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT].label,
            config: {
                outTradeNo: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_TEMPLATE_MSG_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_TEMPLATE_MSG_ACTION].label,
            config: {
                personList: [],
                templateId: '',
                values: [],
                url: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_SUBSCRIPTION_MSG_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_ACTION].label,
            config: {
                templateIds: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION].label,
            config: {
                personList: [],
                templateId: '',
                values: [],
                page: {
                    openPageId: '',
                    pathParameters: [],
                    queryParameters: []
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    OPEN_WECHAT_CUSTOMER_SERVICE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE,
            name: nodeTypeMap[OtherPlatformNodeType.OPEN_WECHAT_CUSTOMER_SERVICE].label,
            config: {
                corpId: {},
                url: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_OPEN_LOCATION_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_OPEN_LOCATION_ACTION].label,
            config: {
                latitude: {},
                longitude: {},
                scale: {},
                name: {},
                address: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_NAVIGATE_TO_MINI_PROGRAM: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_NAVIGATE_TO_MINI_PROGRAM].label,
            config: {
                appId: {},
                path: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_OPEN_CHANNELS_ACTIVITY: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_OPEN_CHANNELS_ACTIVITY].label,
            config: {
                finderUserName: {},
                feedId: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_OPEN_AUDIO_PLAYER: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_OPEN_AUDIO_PLAYER,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_OPEN_AUDIO_PLAYER].label,
            config: {
                src: {},
                title: {},
                epname: {},
                singer: {},
                coverImgUrl: {},
                webUrl: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_GENERATE_MINIPROGRAM_CODE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE].label,
            config: {
                path: {},
                scene: {},
                width: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_UPLOAD_SHIPPING_INFO: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO].label,
            config: {
                orderNumberType: 1,
                transactionId: {},
                outTradeNo: {},
                openid: {},
                logisticsType: 1,
                deliveryMode: 1,
                isAllDelivered: {},
                trackingNo: {},
                expressCompany: {},
                itemDesc: {},
                receiverContact: {},
                consignorContact: {},
                shippingListNodeId: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_GET_ORDER_SHIPPING: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING].label,
            config: {
                orderNumberType: 2,
                transactionId: {},
                merchantTradeNo: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_GET_ORDER_LIST: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_GET_ORDER_LIST,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_GET_ORDER_LIST].label,
            config: {
                openid: {},
                payTimeBegin: {},
                payTimeEnd: {},
                lastIndex: {},
                pageSize: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    WECHAT_FOLLOW_WAYBILL: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL,
            name: nodeTypeMap[OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL].label,
            config: {
                openid: {},
                waybillId: {},
                transId: {},
                receiverPhone: {},
                senderPhone: {},
                orderDetailPath: {},
                deliveryId: {},
                goodsName: {},
                goodsImgUrl: {},
                goodsDesc: {},
                goodsListNodeId: ''
            }
        },
        position: { x: 0, y: 0 }
    }),
    STRIPE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.STRIPE,
            name: nodeTypeMap[OtherPlatformNodeType.STRIPE].label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.UPSTREAM,
                nodeId: '',
                dataSourceId: '',
                sorts: [],

                descFieldId: '',
                currency: '',
                autoRedirectAfterPayment: true,
                /** 订单金额 */
                amountFieldId: '',
                productNameFieldId: '',
                productPictureFieldId: '',
                succeedFieldSettings: [],
                callbackWorkflow: {
                    workflowId: '',
                    workflowArgs: []
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    REFRESH_PAGE: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.REFRESH_PAGE,
            name: nodeTypeMap[OperatorNodeType.REFRESH_PAGE].label,
            config: {}
        },
        position: { x: 0, y: 0 }
    }),
    REFRESH_USER_PERMISSIONS: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.REFRESH_USER_PERMISSIONS,
            name: nodeTypeMap[OperatorNodeType.REFRESH_USER_PERMISSIONS].label,
            config: {}
        },
        position: { x: 0, y: 0 }
    }),
    LOOP_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: FlowControlNodeType.LOOP_ACTION,
            name: nodeTypeMap[FlowControlNodeType.LOOP_ACTION].label,
            config: {
                loopType: LoopTypeEnum.BASE_ON_TIMES,
                timesConfig: {
                    start: {
                        type: VariableType.VALUE,
                        valueVariable: {
                            type: 'number',
                            value: 1
                        }
                    },
                    end: {
                        type: VariableType.VALUE,
                        valueVariable: {
                            type: 'number',
                            value: 10
                        }
                    },
                    step: {
                        type: VariableType.VALUE,
                        valueVariable: {
                            type: 'number',
                            value: 1
                        }
                    }
                },
                loopErrorHandler: LoopErrorHandlerEnum.CONTINUE
            }
        },
        position: { x: 0, y: 0 }
    }),
    HANDLE_SUB_PROCESS: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: FlowControlNodeType.HANDLE_SUB_PROCESS,
            name: nodeTypeMap[FlowControlNodeType.HANDLE_SUB_PROCESS].label,
            config: {
                subProcessId: '',
                args: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    SEND_WEBSOCKET: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: FlowControlNodeType.SEND_WEBSOCKET,
            name: nodeTypeMap[FlowControlNodeType.SEND_WEBSOCKET].label,
            config: {
                eventName: '',
                eventValue: '',
                args: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    CLOSE_FLOAT: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.CLOSE_FLOAT,
            name: nodeTypeMap[OperatorNodeType.CLOSE_FLOAT].label,
            config: {}
        },
        position: { x: 0, y: 0 }
    }),
    DELETE_RECORD: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: ActionDataNodeType.DELETE_RECORD,
            name: nodeTypeMap[ActionDataNodeType.DELETE_RECORD]?.label,
            config: {
                filter: {},
                selectType: FlowSelectSourceType.DATASOURCE,
                nodeId: '',
                dataSourceId: '',
                sorts: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    CALL: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OperatorNodeType.CALL,
            name: nodeTypeMap[OperatorNodeType.CALL].label,
            config: {
                phone: {
                    type: VariableType.VALUE,
                    valueVariable: {
                        type: 'phoneNumber',
                        value: ''
                    }
                }
            }
        },
        position: { x: 0, y: 0 }
    }),
    GOOGLE_SEARCH_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.GOOGLE_SEARCH_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.GOOGLE_SEARCH_ACTION].label,
            config: {
                query: {},
                numberOfResult: 10,
                resultType: SearchApiResultType.TEXT
            }
        },
        position: { x: 0, y: 0 }
    }),
    BAIDU_SEARCH_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.BAIDU_SEARCH_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.BAIDU_SEARCH_ACTION].label,
            config: {
                query: {},
                numberOfResult: 10,
                resultType: SearchApiResultType.TEXT
            }
        },
        position: { x: 0, y: 0 }
    }),
    BING_SEARCH_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: OtherPlatformNodeType.BING_SEARCH_ACTION,
            name: nodeTypeMap[OtherPlatformNodeType.BING_SEARCH_ACTION].label,
            config: {
                query: {},
                numberOfResult: 10,
                resultType: SearchApiResultType.TEXT
            }
        },
        position: { x: 0, y: 0 }
    }),
    CODE_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: AINodeType.CODE_ACTION,
            name: nodeTypeMap[AINodeType.CODE_ACTION].label,
            config: {
                inputVariables: [],
                code: '',
                args: []
            }
        },
        position: { x: 0, y: 0 }
    }),
    LLM_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: AINodeType.LLM_ACTION,
            name: nodeTypeMap[AINodeType.LLM_ACTION].label,
            config: {
                model: '',
                inputVariables: [],
                systemPrompt: {},
                userPrompt: {}
            }
        },
        position: { x: 0, y: 0 }
    }),
    PARAM_EXTRACT_ACTION: () => ({
        id: '',
        type: ClassNode.ACTION,
        data: {
            nodeType: AINodeType.PARAM_EXTRACT_ACTION,
            name: nodeTypeMap[AINodeType.PARAM_EXTRACT_ACTION].label,
            config: {
                model: '',
                input: {},
                args: [],
                prompt: {}
            }
        },
        position: { x: 0, y: 0 }
    })
}

// export const nodeTypeLabelMap: Record<NodeTypes, string> = {
//     EMPTY_TRIGGER: '开始'
// }

export const nodeTypeIconMap: Record<NodeTypes, string> = {
    EMPTY_TRIGGER: 'HomeLiner',
    CREATE_RECORD_TRIGGER: 'HomeLiner',
    UPDATE_RECORD_TRIGGER: 'HomeLiner',
    MATCH_CONDITION_TRIGGER: 'HomeLiner',
    WEBHOOK_TRIGGER: 'HomeLiner',
    SCHEDULED_TRIGGER: 'HomeLiner',
    CLICK_TRIGGER: 'HomeLiner',
    WEBSOCKET_TRIGGER: 'HomeLiner',
    ARG_TRIGGER: 'Reorder',
    CREATE_SINGLE_RECORD_ACTION: 'Add',
    CREATE_RECORD_ACTION: 'AddList',
    UPDATE_RECORD_ACTION: 'Angle',
    FIND_SINGLE_RECORD_ACTION: 'Search',
    FIND_RECORD_ACTION: 'QueryData',
    IMPORT_EXCEL: 'FileXsl',
    BATCH_IMPORT_FILE: 'importFile',
    CONDITION: 'ActiveNodeIF',
    END: 'Golf',
    OPEN_LINK: 'PropertyLink',
    OPEN_PAGE: 'OpenPage',
    GO_BACK: 'Undo',
    NOTIFICATION: 'Bell',
    COPY: 'Copy',
    DELETE_RECORD: 'Trash',
    DOWNLOAD_FILE: 'Download',
    WX_ROBOT_ACTION: 'BrandLogoqiyeweixin',
    DINGTALK_ROBOT_ACTION: 'BrandLogoDingding',
    SEND_EMAIL_ACTION: 'BrandLogoWeCom',
    STATION_MESSAGE_ACTION: 'StationMessage',
    LOOP_ACTION: 'Repeat',
    ALIYUN_MESSAGE_ACTION: 'aliyun',
    CALL: 'PropertyPhone',
    WECHAT_PAY: 'WechatPay',
    WECHAT_REFUND: 'WechatPay',
    WECHAT_REFUND_QUERY: 'WechatPay',
    WECHAT_ADD_RECEIVER: 'WechatPay',
    WECHAT_DELETE_RECEIVER: 'WechatPay',
    WECHAT_PROFIT_SHARING: 'WechatPay',
    WECHAT_PROFIT_SHARING_QUERY: 'WechatPay',
    WECHAT_PROFIT_SHARING_QUERY_AMOUNT: 'WechatPay',
    WECHAT_TEMPLATE_MSG_ACTION: 'WeChatLogo',
    WECHAT_SUBSCRIPTION_MSG_ACTION: 'MiniProgram',
    WECHAT_SUBSCRIPTION_MSG_NOTIFICATION_ACTION: 'MiniProgram',
    WECHAT_OPEN_LOCATION_ACTION: 'MiniProgram',
    WECHAT_NAVIGATE_TO_MINI_PROGRAM: 'MiniProgram',
    WECHAT_OPEN_CHANNELS_ACTIVITY: 'MiniProgram',
    WECHAT_OPEN_AUDIO_PLAYER: 'MiniProgram',
    OPEN_WECHAT_CUSTOMER_SERVICE: 'MiniProgram',
    WECHAT_GENERATE_MINIPROGRAM_CODE: 'MiniProgram',
    WECHAT_UPLOAD_SHIPPING_INFO: 'MiniProgram',
    WECHAT_GET_ORDER_SHIPPING: 'MiniProgram',
    WECHAT_GET_ORDER_LIST: 'MiniProgram',
    WECHAT_FOLLOW_WAYBILL: 'MiniProgram',
    STRIPE: 'Stripe',
    REFRESH_PAGE: 'UpdatePage',
    HANDLE_SUB_PROCESS: 'Reorder',
    SEND_WEBSOCKET: 'HomeLiner',
    CLOSE_FLOAT: 'CloseOverlay',
    REFRESH_USER_PERMISSIONS: 'PermissionSetting',
    GOOGLE_SEARCH_ACTION: 'SearchAPI',
    BAIDU_SEARCH_ACTION: 'SearchAPI',
    BING_SEARCH_ACTION: 'SearchAPI',
    CODE_ACTION: 'Code',
    PARAM_EXTRACT_ACTION: 'Blur',
    LLM_ACTION: 'LLM'
}

export const nodeTypeIconPlatformMap: Record<SenderAccountType, string> = {
    WE_COM_MAIL: 'BrandLogoWeCom',
    NET_EASE_126_MAIL: 'NetEasyFree'
}

export const copyTypeOptions = [
    { label: '自定义', value: 'CUSTOM' }
    // { label: '当前记录链接', value: 'CURRENT_RECORD' },
    // { label: '当前页面链接', value: 'CURRENT_PAGE' }
]

export const flowSelectSourceTypeMap: { label: string; value: FlowSelectSourceType }[] = [
    { label: '数据表', value: FlowSelectSourceType.DATASOURCE },
    { label: '上游节点', value: FlowSelectSourceType.UPSTREAM }
]

export const notificationStatusOptions = [
    { label: '中立', value: 'INFO' },
    { label: '成功', value: 'SUCCESS' },
    { label: '失败', value: 'ERROR' }
]

/** 输出为特定数据的节点 */
export const OUTPUT_SPECIFIC_DATA_NODES: Set<NodeTypes> = new Set<NodeTypes>([
    AINodeType.LLM_ACTION,
    OtherPlatformNodeType.BAIDU_SEARCH_ACTION,
    OtherPlatformNodeType.BING_SEARCH_ACTION,
    OtherPlatformNodeType.GOOGLE_SEARCH_ACTION,
    OtherPlatformNodeType.WECHAT_REFUND,
    OtherPlatformNodeType.WECHAT_REFUND_QUERY,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT,
    OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE,
    OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO,
    OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING,
    OtherPlatformNodeType.WECHAT_GET_ORDER_LIST,
    OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL,
    ActionDataNodeType.IMPORT_EXCEL,
    TriggerNodeType.WEBHOOK_TRIGGER
])

/** 输出为结束节点的数据 */
export const OUTPUT_FLOW_DATA_NODES: Set<NodeTypes> = new Set<NodeTypes>([FlowControlNodeType.HANDLE_SUB_PROCESS])

export const OUTPUT_ARGS_END_NODES: Set<NodeTypes> = new Set<NodeTypes>([EndNodeType.END])

/** 输出为单条记录的节点 */
export const OUTPUT_SINGLE_RECORD_NODES = new Set<NodeTypes>([
    TriggerNodeType.CREATE_RECORD_TRIGGER,
    TriggerNodeType.UPDATE_RECORD_TRIGGER,
    TriggerNodeType.MATCH_CONDITION_TRIGGER,
    ActionDataNodeType.CREATE_SINGLE_RECORD_ACTION,
    ActionDataNodeType.UPDATE_RECORD_ACTION,
    ActionDataNodeType.FIND_SINGLE_RECORD_ACTION,
    OtherPlatformNodeType.WECHAT_PAY,
    OtherPlatformNodeType.STRIPE
])

/** 输出为多条记录的节点 */
export const OUTPUT_MULTIPLE_RECORD_NODES: Set<NodeTypes> = new Set<NodeTypes>([
    ActionDataNodeType.FIND_RECORD_ACTION,
    ActionDataNodeType.CREATE_RECORD_ACTION
])

export const OUTPUT_MULTIPLE_ARGS_NODES: Set<NodeTypes> = new Set<NodeTypes>([
    OtherPlatformNodeType.BAIDU_SEARCH_ACTION,
    OtherPlatformNodeType.GOOGLE_SEARCH_ACTION,
    OtherPlatformNodeType.BING_SEARCH_ACTION,
    OtherPlatformNodeType.WECHAT_GET_ORDER_LIST
])

/** 输出为记录的节点 */
export const OUTPUT_RECORD_NODES = new Set<NodeTypes>([...OUTPUT_SINGLE_RECORD_NODES, ...OUTPUT_MULTIPLE_RECORD_NODES])

/** 输出为单条参数的节点 */
export const OUTPUT_SINGLE_ARGS_NODES: Set<NodeTypes> = new Set<NodeTypes>([
    TriggerNodeType.CLICK_TRIGGER,
    TriggerNodeType.ARG_TRIGGER,
    TriggerNodeType.WEBSOCKET_TRIGGER,
    TriggerNodeType.WEBHOOK_TRIGGER,
    AINodeType.CODE_ACTION,
    AINodeType.PARAM_EXTRACT_ACTION,
    AINodeType.LLM_ACTION,
    ActionDataNodeType.IMPORT_EXCEL,
    OtherPlatformNodeType.WECHAT_REFUND,
    OtherPlatformNodeType.WECHAT_REFUND_QUERY,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY,
    OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT,
    OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE,
    OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO,
    OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING,
    OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL,
    FlowControlNodeType.HANDLE_SUB_PROCESS,
    EndNodeType.END
])

/** 输出为参数的节点 */
export const OUTPUT_ARGS_NODES: Set<NodeTypes> = new Set<NodeTypes>([...OUTPUT_SINGLE_ARGS_NODES, ...OUTPUT_MULTIPLE_ARGS_NODES])

/** 输出为单条数据(args + record)的节点 */
export const OUTPUT_SINGLE_DATA_NODES = new Set<NodeTypes>([...OUTPUT_SINGLE_RECORD_NODES, ...OUTPUT_SINGLE_ARGS_NODES])
/** 输出为多条数据(args + record)的节点 */
export const OUTPUT_MULTIPLE_DATA_NODES = new Set<NodeTypes>([...OUTPUT_MULTIPLE_RECORD_NODES, ...OUTPUT_MULTIPLE_ARGS_NODES])
/** 输出为数据的节点 */
export const OUTPUT_DATA_NODES = new Set<NodeTypes>([...OUTPUT_SINGLE_DATA_NODES, ...OUTPUT_MULTIPLE_DATA_NODES])

export const TRIGGER_NODES: Set<NodeTypes> = new Set<NodeTypes>([
    TriggerNodeType.UPDATE_RECORD_TRIGGER,
    TriggerNodeType.MATCH_CONDITION_TRIGGER,
    TriggerNodeType.CREATE_RECORD_TRIGGER,
    TriggerNodeType.CLICK_TRIGGER,
    TriggerNodeType.SCHEDULED_TRIGGER,
    TriggerNodeType.WEBHOOK_TRIGGER
])

export const loopTypeMap = [
    {
        label: '按指定循环次数',
        value: LoopTypeEnum.BASE_ON_TIMES
    },
    {
        label: '按查询到的记录循环',
        value: LoopTypeEnum.BASE_ON_RECORDS
    }
]

export const OutputSpecificDataNodeList: Record<
    Extract<
        NodeTypes,
        | 'LLM_ACTION'
        | 'GOOGLE_SEARCH_ACTION'
        | 'BAIDU_SEARCH_ACTION'
        | 'BING_SEARCH_ACTION'
        | 'IMPORT_EXCEL'
        | 'WECHAT_REFUND'
        | 'WECHAT_REFUND_QUERY'
        | 'WECHAT_ADD_RECEIVER'
        | 'WECHAT_DELETE_RECEIVER'
        | 'WECHAT_PROFIT_SHARING'
        | 'WECHAT_PROFIT_SHARING_QUERY'
        | 'WECHAT_PROFIT_SHARING_QUERY_AMOUNT'
        | 'WEBHOOK_TRIGGER'
        | 'WECHAT_GENERATE_MINIPROGRAM_CODE'
        | 'WECHAT_UPLOAD_SHIPPING_INFO'
        | 'WECHAT_GET_ORDER_SHIPPING'
        | 'WECHAT_GET_ORDER_LIST'
        | 'WECHAT_FOLLOW_WAYBILL'
    >,
    ArgumentsSchema[]
> = {
    [AINodeType.LLM_ACTION]: [
        {
            name: 'text',
            innerType: 'TEXT',
            id: LLMArgId
        }
    ],
    [OtherPlatformNodeType.GOOGLE_SEARCH_ACTION]: [
        {
            name: 'label',
            innerType: 'TEXT',
            id: SearchArgTitleId,
            desc: '搜索结果的标题'
        },
        {
            name: 'value',
            innerType: 'TEXT',
            id: SearchArgContentId,
            desc: '搜索结果的链接'
        }
    ],
    [OtherPlatformNodeType.BAIDU_SEARCH_ACTION]: [
        {
            name: 'label',
            innerType: 'TEXT',
            id: SearchArgTitleId,
            desc: '搜索结果的标题'
        },
        {
            name: 'value',
            innerType: 'TEXT',
            id: SearchArgContentId,
            desc: '搜索结果的链接'
        }
    ],
    [OtherPlatformNodeType.BING_SEARCH_ACTION]: [
        {
            name: 'label',
            innerType: 'TEXT',
            id: SearchArgTitleId,
            desc: '搜索结果的标题'
        },
        {
            name: 'value',
            innerType: 'TEXT',
            id: SearchArgContentId,
            desc: '搜索结果的链接'
        }
    ],
    [OtherPlatformNodeType.WECHAT_REFUND]: [
        {
            id: RefundIdId,
            name: '微信支付退款单号',
            desc: '申请退款受理成功时，该笔退款单在微信支付侧生成的唯一标识。',
            innerType: 'TEXT'
        },
        {
            id: OutRefundNoId,
            name: '商户退款单号',
            desc: '商户系统内部的退款单号，商户系统内部唯一。',
            innerType: 'TEXT'
        },
        {
            id: OutTradeNo,
            name: '商户订单号',
            desc: '商户下单时传入的商户系统内部订单号。',
            innerType: 'TEXT'
        },
        {
            id: ChannelId,
            name: '退款渠道',
            desc: `订单退款渠道
以下枚举：
ORIGINAL: 原路退款
BALANCE: 退回到余额
OTHER_BALANCE: 原账户异常退到其他余额账户
OTHER_BANKCARD: 原银行卡异常退到其他银行卡(发起异常退款成功后返回)`,
            innerType: 'TEXT'
        },
        {
            id: UserReceivedAccountId,
            name: '退款入账账户',
            desc: ` 取当前退款单的退款入账方，有以下几种情况：
1）退回银行卡：{银行名称}{卡类型}{卡尾号}
2）退回支付用户零钱:支付用户零钱
3）退还商户:商户基本账户商户结算银行账户
4）退回支付用户零钱通:支付用户零钱通
5）退回支付用户银行电子账户:支付用户银行电子账户
6）退回支付用户零花钱:支付用户零花钱
7）退回用户经营账户:用户经营账户
8）退回支付用户来华零钱包:支付用户来华零钱包
9）退回企业支付商户:企业支付商户
10）退回支付用户小金罐:支付用户小金罐`,
            innerType: 'TEXT'
        },
        {
            id: SuccessTimeId,
            name: '退款成功时间',
            desc: `1、定义：退款成功的时间，该字段在退款状态status为SUCCESS（退款成功）时返回。
2、格式：遵循rfc3339标准格式：yyyy-MM-DDTHH:mm:ss+TIMEZONE。yyyy-MM-DD 表示年月日；T 字符用于分隔日期和时间部分；HH:mm:ss 表示具体的时分秒；TIMEZONE 表示时区（例如，+08:00 对应东八区时间，即北京时间）。
示例：2015-05-20T13:29:35+08:00 表示北京时间2015年5月20日13点29分35秒。`,
            innerType: 'TEXT'
        },
        {
            id: CreateTimeId,
            name: '退款创建时间',
            desc: `1、定义：提交退款申请成功，微信受理退款申请单的时间。
2、格式：遵循rfc3339标准格式：yyyy-MM-DDTHH:mm:ss+TIMEZONE。yyyy-MM-DD 表示年月日；T 字符用于分隔日期和时间部分；HH:mm:ss 表示具体的时分秒；TIMEZONE 表示时区（例如，+08:00 对应东八区时间，即北京时间）。
示例：2015-05-20T13:29:35+08:00 表示北京时间2015年5月20日13点29分35秒。`,
            innerType: 'TEXT'
        },
        {
            id: StatusId,
            name: '退款状态',
            desc: `退款单的退款处理状态。
SUCCESS: 退款成功
CLOSED: 退款关闭
PROCESSING: 退款处理中
ABNORMAL: 退款异常，退款到银行发现用户的卡作废或者冻结了，导致原路退款银行卡失败，可前往商户平台-交易中心，手动处理此笔退款，可参考： 退款异常的处理，或者通过发起异常退款接口进行处理。
注：状态流转说明请参考状态流转图`,
            innerType: 'TEXT'
        },
        {
            id: FundsAccountId,
            name: '资金账户',
            desc: ` 退款所使用资金对应的资金账户类型
UNSETTLED: 未结算资金
AVAILABLE: 可用余额
UNAVAILABLE: 不可用余额
OPERATION: 运营账户
BASIC: 基本账户（含可用余额和不可用余额）
ECNY_BASIC: 数字人民币基本账户`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_REFUND_QUERY]: [
        {
            id: RefundIdId,
            name: '微信支付退款单号',
            desc: '申请退款受理成功时，该笔退款单在微信支付侧生成的唯一标识。',
            innerType: 'TEXT'
        },
        {
            id: OutRefundNoId,
            name: '商户退款单号',
            desc: '商户系统内部的退款单号，商户系统内部唯一。',
            innerType: 'TEXT'
        },
        {
            id: OutTradeNo,
            name: '商户订单号',
            desc: '商户下单时传入的商户系统内部订单号。',
            innerType: 'TEXT'
        },
        {
            id: ChannelId,
            name: '退款渠道',
            desc: `订单退款渠道
以下枚举：
ORIGINAL: 原路退款
BALANCE: 退回到余额
OTHER_BALANCE: 原账户异常退到其他余额账户
OTHER_BANKCARD: 原银行卡异常退到其他银行卡(发起异常退款成功后返回)`,
            innerType: 'TEXT'
        },
        {
            id: UserReceivedAccountId,
            name: '退款入账账户',
            desc: `取当前退款单的退款入账方，有以下几种情况：
1）退回银行卡：{银行名称}{卡类型}{卡尾号}
2）退回支付用户零钱:支付用户零钱
3）退还商户:商户基本账户商户结算银行账户
4）退回支付用户零钱通:支付用户零钱通
5）退回支付用户银行电子账户:支付用户银行电子账户
6）退回支付用户零花钱:支付用户零花钱
7）退回用户经营账户:用户经营账户
8）退回支付用户来华零钱包:支付用户来华零钱包
9）退回企业支付商户:企业支付商户
10）退回支付用户小金罐:支付用户小金罐`,
            innerType: 'TEXT'
        },
        {
            id: SuccessTimeId,
            name: '退款成功时间',
            desc: `1、定义：退款成功的时间，该字段在退款状态status为SUCCESS（退款成功）时返回。
2、格式：遵循rfc3339标准格式：yyyy-MM-DDTHH:mm:ss+TIMEZONE。yyyy-MM-DD 表示年月日；T 字符用于分隔日期和时间部分；HH:mm:ss 表示具体的时分秒；TIMEZONE 表示时区（例如，+08:00 对应东八区时间，即北京时间）。
示例：2015-05-20T13:29:35+08:00 表示北京时间2015年5月20日13点29分35秒。`,
            innerType: 'TEXT'
        },
        {
            id: CreateTimeId,
            name: '退款创建时间',
            desc: `1、定义：提交退款申请成功，微信受理退款申请单的时间。
2、格式：遵循rfc3339标准格式：yyyy-MM-DDTHH:mm:ss+TIMEZONE。yyyy-MM-DD 表示年月日；T 字符用于分隔日期和时间部分；HH:mm:ss 表示具体的时分秒；TIMEZONE 表示时区（例如，+08:00 对应东八区时间，即北京时间）。
示例：2015-05-20T13:29:35+08:00 表示北京时间2015年5月20日13点29分35秒。`,
            innerType: 'TEXT'
        },
        {
            id: StatusId,
            name: '退款状态',
            desc: `退款单的退款处理状态。
SUCCESS: 退款成功
CLOSED: 退款关闭
PROCESSING: 退款处理中
ABNORMAL: 退款异常，退款到银行发现用户的卡作废或者冻结了，导致原路退款银行卡失败，可前往商户平台-交易中心，手动处理此笔退款，可参考： 退款异常的处理，或者通过发起异常退款接口进行处理。
注：状态流转说明请参考状态流转图`,
            innerType: 'TEXT'
        },
        {
            id: FundsAccountId,
            name: '资金账户',
            desc: `退款所使用资金对应的资金账户类型
UNSETTLED: 未结算资金
AVAILABLE: 可用余额
UNAVAILABLE: 不可用余额
OPERATION: 运营账户
BASIC: 基本账户（含可用余额和不可用余额）
ECNY_BASIC: 数字人民币基本账户`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_ADD_RECEIVER]: [
        {
            id: TypeId,
            name: '接收方类型',
            desc: `可选取值
MERCHANT_ID: 商户号
PERSONAL_OPENID: 个人openid`,
            innerType: 'TEXT'
        },
        {
            id: AccountId,
            name: '接收方账号',
            desc: `分账接收方类型为MERCHANT_ID时，分账接收方账号为商户号
分账接收方类型为PERSONAL_OPENID时，分账接收方账号为个人OpenID（由商户的AppID转换得到）`,
            innerType: 'TEXT'
        },
        {
            id: NameId,
            name: '分账接收方全称',
            desc: `分账接收方类型是商户时，是商户全称（必传）
当商户是小微商户或个体户时，是开户人姓名
分账接收方类型是个人时，是个人姓名`,
            innerType: 'TEXT'
        },
        {
            id: RelationTypeId,
            name: '与分账方的关系类型',
            desc: `分账发起方商户与分账接收方的关系。可选取值：
STORE: 门店
STAFF: 员工
STORE_OWNER: 店主
PARTNER: 合作伙伴
HEADQUARTER: 总部
BRAND: 品牌方
DISTRIBUTOR: 分销商
USER: 用户
SUPPLIER: 供应商
CUSTOM: 自定义`,
            innerType: 'TEXT'
        },
        {
            id: CustomRelationId,
            name: '自定义的分账关系',
            desc: `特约商户与接收方具体的关系，本字段最多10个字。
当字段relation_type的值为CUSTOM时，本字段必填
当字段relation_type的值不为CUSTOM时，本字段无需填写`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_DELETE_RECEIVER]: [
        {
            id: TypeId,
            name: '接收方类型',
            desc: `可选取值
MERCHANT_ID: 商户号
PERSONAL_OPENID: 个人openid`,
            innerType: 'TEXT'
        },
        {
            id: AccountId,
            name: '接收方账号',
            desc: `分账接收方类型为MERCHANT_ID时，分账接收方账号为商户号
分账接收方类型为PERSONAL_OPENID时，分账接收方账号为个人OpenID（由商户的AppID转换得到）`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_PROFIT_SHARING]: [
        {
            id: OutTradeNo,
            name: '商户订单号',
            desc: '商户下单时传入的商户系统内部订单号。',
            innerType: 'TEXT'
        },
        {
            id: OutOrderNoId,
            name: '商户分账单号',
            desc: '商户系统内部的分账单号，在商户系统内部唯一，同一分账单号多次请求等同一次。',
            innerType: 'TEXT'
        },
        {
            id: OrderIdId,
            name: '微信分账单号',
            desc: '微信分账单号，微信系统返回的唯一标识',
            innerType: 'TEXT'
        },
        {
            id: StateId,
            name: '分账单状态',
            desc: `分账单状态。可选值：
PROCESSING: 处理中
FINISHED: 分账完成`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY]: [
        {
            id: OutTradeNo,
            name: '商户订单号',
            desc: '商户下单时传入的商户系统内部订单号。',
            innerType: 'TEXT'
        },
        {
            id: OutOrderNoId,
            name: '商户分账单号',
            desc: '商户系统内部的分账单号，在商户系统内部唯一，同一分账单号多次请求等同一次。',
            innerType: 'TEXT'
        },
        {
            id: OrderIdId,
            name: '微信分账单号',
            desc: '微信分账单号，微信系统返回的唯一标识',
            innerType: 'TEXT'
        },
        {
            id: StateId,
            name: '分账单状态',
            desc: `分账单状态。可选值：
PROCESSING: 处理中
FINISHED: 分账完成`,
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_PROFIT_SHARING_QUERY_AMOUNT]: [
        {
            id: OutTradeNo,
            name: '商户订单号',
            desc: '商户下单时传入的商户系统内部订单号。',
            innerType: 'TEXT'
        },
        {
            id: UnSplitAmountId,
            name: '订单剩余待分金额',
            desc: '订单剩余待分金额，单位是元精确到分',
            innerType: 'NUMBER'
        }
    ],
    [ActionDataNodeType.IMPORT_EXCEL]: [
        {
            id: ImportExcelTotalCountId,
            name: ImportExcelTotalCountId,
            desc: '导入总条数',
            innerType: 'NUMBER'
        },
        {
            id: ImportExcelImportedRecordsId,
            name: ImportExcelImportedRecordsId,
            desc: '导入的多条记录的编号值数组',
            innerType: 'ARRAY'
        },
        {
            id: ImportExcelIsImportAllId,
            name: ImportExcelIsImportAllId,
            desc: '是否全部导入成功',
            innerType: 'BOOL'
        },
        {
            id: ImportExcelReasonsForFailedDataId,
            name: ImportExcelReasonsForFailedDataId,
            desc: '导入失败的原因',
            innerType: 'TEXT'
        }
    ],
    [TriggerNodeType.WEBHOOK_TRIGGER]: [
        {
            id: WebhookBody,
            name: 'Webhook Body',
            desc: 'Webhook Body',
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_GENERATE_MINIPROGRAM_CODE]: [
        {
            id: MiniProgramCode,
            name: 'image_url',
            desc: '小程序码图片地址',
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_UPLOAD_SHIPPING_INFO]: [
        {
            id: 'errcode',
            name: '错误码',
            desc: '成功为 0',
            innerType: 'NUMBER'
        },
        {
            id: 'errmsg',
            name: '错误信息',
            desc: '成功为 ok',
            innerType: 'TEXT'
        },
        {
            id: 'outTradeNo',
            name: '商户订单号',
            desc: '实际使用的商户订单号，便于后续传运单',
            innerType: 'TEXT'
        },
        {
            id: 'transactionId',
            name: '微信支付单号',
            desc: '实际使用的微信支付单号，便于后续传运单',
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_GET_ORDER_SHIPPING]: [
        {
            id: 'transactionId',
            name: '微信支付单号',
            desc: '微信支付侧订单号',
            innerType: 'TEXT'
        },
        {
            id: 'merchantTradeNo',
            name: '商户订单号',
            desc: '商户系统内部订单号',
            innerType: 'TEXT'
        },
        {
            id: 'merchantId',
            name: '商户号',
            desc: '微信支付商户号',
            innerType: 'TEXT'
        },
        {
            id: 'openid',
            name: '支付者openid',
            desc: '支付者小程序 openid',
            innerType: 'TEXT'
        },
        {
            id: 'description',
            name: '商品描述',
            desc: '商品描述',
            innerType: 'TEXT'
        },
        {
            id: 'paidAmount',
            name: '支付金额(分)',
            desc: '用户支付金额，单位分',
            innerType: 'NUMBER'
        },
        {
            id: 'payTime',
            name: '支付时间',
            desc: '支付完成时间，Unix 秒级时间戳',
            innerType: 'NUMBER'
        },
        {
            id: 'orderState',
            name: '订单状态',
            desc: '1 待发货，2 已发货，3 确认收货，4 交易完成，5 已退款，6 资金待结算',
            innerType: 'NUMBER'
        },
        {
            id: 'orderStateText',
            name: '订单状态文案',
            desc: '订单状态对应中文',
            innerType: 'TEXT'
        },
        {
            id: 'inComplaint',
            name: '是否纠纷中',
            desc: '是否处于售后纠纷中',
            innerType: 'BOOL'
        },
        {
            id: 'finishShipping',
            name: '是否已全部发货',
            desc: '是否已全部发货',
            innerType: 'BOOL'
        },
        {
            id: 'logisticsType',
            name: '物流模式',
            desc: '1 快递，2 同城，3 虚拟，4 自提',
            innerType: 'NUMBER'
        },
        {
            id: 'deliveryMode',
            name: '发货模式',
            desc: '1 统一发货，2 分拆发货',
            innerType: 'NUMBER'
        },
        {
            id: 'trackingNo',
            name: '物流单号',
            desc: '统一发货取第一条物流单号',
            innerType: 'TEXT'
        },
        {
            id: 'expressCompany',
            name: '物流公司编码',
            desc: '统一发货取第一条物流公司编码',
            innerType: 'TEXT'
        },
        {
            id: 'shippingListJson',
            name: '物流信息列表JSON',
            desc: '完整物流列表 JSON 文本，多包裹时使用',
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_GET_ORDER_LIST]: [
        {
            id: 'transactionId',
            name: '微信支付单号',
            desc: '微信支付侧订单号',
            innerType: 'TEXT'
        },
        {
            id: 'merchantTradeNo',
            name: '商户订单号',
            desc: '商户系统内部订单号',
            innerType: 'TEXT'
        },
        {
            id: 'merchantId',
            name: '商户号',
            desc: '微信支付商户号',
            innerType: 'TEXT'
        },
        {
            id: 'openid',
            name: '支付者openid',
            desc: '支付者小程序 openid',
            innerType: 'TEXT'
        },
        {
            id: 'description',
            name: '商品描述',
            desc: '商品描述',
            innerType: 'TEXT'
        },
        {
            id: 'paidAmount',
            name: '支付金额(分)',
            desc: '用户支付金额，单位分',
            innerType: 'NUMBER'
        },
        {
            id: 'payTime',
            name: '支付时间',
            desc: '支付完成时间，Unix 秒级时间戳',
            innerType: 'NUMBER'
        },
        {
            id: 'orderState',
            name: '订单状态',
            desc: '1 待发货，2 已发货，3 确认收货，4 交易完成，5 已退款，6 资金待结算',
            innerType: 'NUMBER'
        },
        {
            id: 'orderStateText',
            name: '订单状态文案',
            desc: '订单状态对应中文',
            innerType: 'TEXT'
        },
        {
            id: 'inComplaint',
            name: '是否纠纷中',
            desc: '是否处于售后纠纷中',
            innerType: 'BOOL'
        },
        {
            id: 'finishShipping',
            name: '是否已全部发货',
            desc: '是否已全部发货',
            innerType: 'BOOL'
        },
        {
            id: 'logisticsType',
            name: '物流模式',
            desc: '1 快递，2 同城，3 虚拟，4 自提',
            innerType: 'NUMBER'
        },
        {
            id: 'deliveryMode',
            name: '发货模式',
            desc: '1 统一发货，2 分拆发货',
            innerType: 'NUMBER'
        },
        {
            id: 'trackingNo',
            name: '物流单号',
            desc: '统一发货取第一条物流单号',
            innerType: 'TEXT'
        },
        {
            id: 'expressCompany',
            name: '物流公司编码',
            desc: '统一发货取第一条物流公司编码',
            innerType: 'TEXT'
        },
        {
            id: 'shippingListJson',
            name: '物流信息列表JSON',
            desc: '完整物流列表 JSON 文本，多包裹时使用',
            innerType: 'TEXT'
        },
        {
            id: 'hasMore',
            name: '是否还有更多',
            desc: '是否还有下一页',
            innerType: 'BOOL'
        },
        {
            id: 'lastIndex',
            name: '翻页游标',
            desc: '用于再次查询下一页',
            innerType: 'TEXT'
        }
    ],
    [OtherPlatformNodeType.WECHAT_FOLLOW_WAYBILL]: [
        {
            id: 'waybillToken',
            name: '运单查询id',
            desc: '微信返回的 waybill_token，可用于后续查运单',
            innerType: 'TEXT'
        },
        {
            id: 'errcode',
            name: '错误码',
            desc: '成功为 0',
            innerType: 'NUMBER'
        },
        {
            id: 'errmsg',
            name: '错误信息',
            desc: '成功为 ok',
            innerType: 'TEXT'
        }
    ]
}
